// LIBRERIE/IMPORTS
import React from 'react';
import { useTranslation } from "react-i18next";

// FOGLI DI STILE
import styles from './Pagination.module.css';

interface PaginationProps {
    count: number;
    limit: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    siblingCount?: number;
}

// Helper per generare range numerici
const range = (start: number, end: number): number[] => {
    const length = end - start + 1;
    return Array.from({ length }, (_, idx) => idx + start);
};

const Pagination: React.FC<PaginationProps> = ({
    count,
    limit,
    currentPage,
    onPageChange,
    siblingCount = 1
}) => {
    const { t } = useTranslation('propertyList');

    const totalPages = count > 0 && limit > 0 ? Math.ceil(count / limit) : 1;

    if (totalPages <= 1) return null;

    const DOTS = '...';
    const totalPageNumbersToShow = siblingCount + 5;
    let pageNumbers: (number | string)[] = [];

    if (totalPages <= totalPageNumbersToShow) {
        pageNumbers = range(1, totalPages);
    } else {
        const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

        const shouldShowLeftDots = leftSiblingIndex > 2;
        const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

        const firstPageIndex = 1;
        const lastPageIndex = totalPages;

        if (!shouldShowLeftDots && shouldShowRightDots) {
            const leftItemCount = 3 + 2 * siblingCount;
            const leftRange = range(1, leftItemCount);
            pageNumbers = [...leftRange, DOTS, lastPageIndex];
        } else if (shouldShowLeftDots && !shouldShowRightDots) {
            const rightItemCount = 3 + 2 * siblingCount;
            const rightRange = range(totalPages - rightItemCount + 1, totalPages);
            pageNumbers = [firstPageIndex, DOTS, ...rightRange];
        } else {
            const middleRange = range(leftSiblingIndex, rightSiblingIndex);
            pageNumbers = [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
        }
    }

    const handlePageClick = (page: number | string) => {
        if (typeof page === 'string') return;

        const topList = document.getElementById('properties-box');

        if (page >= 1 && page <= totalPages && page !== currentPage) {
            onPageChange(page);
            if (topList) {
                window.scrollTo(0, topList.getBoundingClientRect().y + window.scrollY);
            }
        }
    };

    const getItemClasses = (page: number | string): string => {
        const classes = [styles.pageItem];
        if (page === DOTS) {
            classes.push(styles.ellipsis, styles.disabled);
        } else if (typeof page === 'number') {
            if (page === currentPage) {
                classes.push(styles.active);
            }
        }
        return classes.join(' ');
    };

    const getControlButtonClasses = (isDisabled: boolean): string => {
        const classes = [styles.pageItem];
        if (isDisabled) {
            classes.push(styles.disabled);
        }
        return classes.join(' ');
    };

    const isFirstDisabled = currentPage === 1;
    const isLastDisabled = currentPage === totalPages;

    const firstPageText = t('propertyList.pagination.firstPage');
    const prevPageText = t('propertyList.pagination.prevPage');
    const nextPageText = t('propertyList.pagination.nextPage');
    const lastPageText = t('propertyList.pagination.lastPage');
    const firstPageAria = t('propertyList.pagination.firstPageAria');
    const prevPageAria = t('propertyList.pagination.prevPageAria');
    const nextPageAria = t('propertyList.pagination.nextPageAria');
    const lastPageAria = t('propertyList.pagination.lastPageAria');
    const gotoPageAriaPattern = t('propertyList.pagination.gotoPageAria');

    return (
        <ul className="pagination">
            {/* Bottone Prima Pagina */}
            <li className={getControlButtonClasses(isFirstDisabled)}>
                <a
                    className={styles.pageLink}
                    href="#"
                    onClick={(e) => { e.preventDefault(); handlePageClick(1); }}
                    aria-label={firstPageAria ?? 'Vai alla prima pagina'}
                    aria-disabled={isFirstDisabled}
                    tabIndex={isFirstDisabled ? -1 : undefined}
                >
                    {firstPageText ?? 'Prima'}
                </a>
            </li>

            {/* Bottone Pagina Precedente */}
            <li className={getControlButtonClasses(isFirstDisabled)}>
                <a
                    className={styles.pageLink}
                    href="#"
                    onClick={(e) => { e.preventDefault(); handlePageClick(currentPage - 1); }}
                    aria-label={prevPageAria ?? 'Vai alla pagina precedente'}
                    aria-disabled={isFirstDisabled}
                    tabIndex={isFirstDisabled ? -1 : undefined}
                >
                    {prevPageText ?? 'Prec.'}
                </a>
            </li>

            {/* Numeri di Pagina e Puntini */}
            {pageNumbers.map((page, index) => (
                <li
                    key={index}
                    className={getItemClasses(page)}
                >
                    {page === DOTS ? (
                        <span className={styles.pageLink} aria-hidden="true">
                            {DOTS}
                        </span>
                    ) : page === currentPage ? (
                        <span className={styles.pageLink} aria-current='page'>
                            {page}
                        </span>
                    ) : (
                        <a
                            className={styles.pageLink}
                            href="#"
                            onClick={(e) => { e.preventDefault(); handlePageClick(page); }}
                            aria-label={t(gotoPageAriaPattern, { page: page, defaultValue: `Vai alla pagina ${page}` }) as string}
                        >
                            {page}
                        </a>
                    )}
                </li>
            ))}

            <li className={getControlButtonClasses(isLastDisabled)}>
                <a
                    className={styles.pageLink}
                    href="#"
                    onClick={(e) => { e.preventDefault(); handlePageClick(currentPage + 1); }}
                    aria-label={nextPageAria ?? 'Vai alla pagina successiva'}
                    aria-disabled={isLastDisabled}
                    tabIndex={isLastDisabled ? -1 : undefined}
                >
                    {nextPageText ?? 'Succ.'}
                </a>
            </li>

            {/* Bottone Ultima Pagina */}
            <li className={getControlButtonClasses(isLastDisabled)}>
                <a
                    className={styles.pageLink}
                    href="#"
                    onClick={(e) => { e.preventDefault(); handlePageClick(totalPages); }}
                    aria-label={lastPageAria ?? "Vai all'ultima pagina"}
                    aria-disabled={isLastDisabled}
                    tabIndex={isLastDisabled ? -1 : undefined}
                >
                    {lastPageText ?? 'Ultima'}
                </a>
            </li>
        </ul>
    );
};

export default Pagination;